import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function EditMatchModal({ session, match, onClose, onSuccess }) {
    const [formData, setFormData] = useState({
        title: '',
        slot_count: 100,
        entry_fee: 10,
        mode: 'solo',
        map: 'Erangel',
        start_ts: '',
        description: '',
        format: 'TPP',
        prize_pool: '',
        per_kill: '',
    });
    const [loading, setLoading] = useState(false);

    // Initialize form with match data
    useEffect(() => {
        if (match) {
            // Format start_ts for datetime-local input (YYYY-MM-DDThh:mm)
            let dateTimeStr = '';
            if (match.start_ts) {
                const date = new Date(match.start_ts);
                // Adjust to local time string expected by input
                // Using strict ISO string slicing often causes timezone issues, 
                // but for simplicity we'll use a helper or local specific formatting
                const offset = date.getTimezoneOffset() * 60000;
                const localISOTime = (new Date(date.getTime() - offset)).toISOString().slice(0, 16);
                dateTimeStr = localISOTime;
            }

            setFormData({
                title: match.title || '',
                slot_count: match.slot_count || 100,
                entry_fee: match.entry_fee || 0,
                mode: match.mode || 'solo',
                map: match.map || 'Erangel',
                start_ts: dateTimeStr,
                description: match.description || '',
                format: match.format || 'TPP',
                prize_pool: match.prize_pool_bigint ? (match.prize_pool_bigint / 100) : '',
                per_kill: match.per_kill || '',
            });
        }
    }, [match]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.start_ts) {
            toast.error('Please fill in all required fields');
            return;
        }

        setLoading(true);
        try {
            // Prioritize admin_token from localStorage (Independent Admin Login)
            const token = localStorage.getItem('admin_token') || session?.access_token;

            const payload = {
                title: formData.title,
                slot_count: parseInt(formData.slot_count),
                entry_fee: parseInt(formData.entry_fee),
                mode: formData.mode,
                map: formData.map,
                start_ts: new Date(formData.start_ts).toISOString(),
                description: formData.description,
                format: formData.format,
                prize_pool_bigint: formData.prize_pool ? parseInt(formData.prize_pool) * 100 : 0,
                per_kill: formData.per_kill ? parseInt(formData.per_kill) : 0,
            };

            const response = await fetch(`${API_BASE}/admin/matches/${match.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update match');
            }

            onSuccess();
        } catch (error) {
            toast.error(error.message || 'Failed to update match');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                <div className="modal-header">
                    <h3 className="modal-title">Edit Match</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Match Title *</label>
                        <input
                            type="text"
                            name="title"
                            className="form-input"
                            placeholder="e.g., Friday Night Tournament"
                            value={formData.title}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                        <div className="form-group">
                            <label className="form-label">Mode *</label>
                            <select
                                name="mode"
                                className="form-input"
                                value={formData.mode}
                                onChange={handleChange}
                                required
                            >
                                <option value="solo">Solo</option>
                                <option value="duo">Duo</option>
                                <option value="squad">Squad</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Map *</label>
                            <select
                                name="map"
                                className="form-input"
                                value={formData.map}
                                onChange={handleChange}
                                required
                            >
                                <option value="Erangel">Erangel</option>
                                <option value="Miramar">Miramar</option>
                                <option value="Sanhok">Sanhok</option>
                                <option value="Vikendi">Vikendi</option>
                                <option value="Livik">Livik</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                        <div className="form-group">
                            <label className="form-label">Total Slots *</label>
                            <input
                                type="number"
                                name="slot_count"
                                className="form-input"
                                min="10"
                                max="200"
                                value={formData.slot_count}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Entry Fee (₹) *</label>
                            <input
                                type="number"
                                name="entry_fee"
                                className="form-input"
                                min="0"
                                value={formData.entry_fee}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                            name="description"
                            className="form-input"
                            placeholder="Match rules, specific instructions, etc."
                            value={formData.description}
                            onChange={handleChange}
                            rows="3"
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                        <div className="form-group">
                            <label className="form-label">Format</label>
                            <select
                                name="format"
                                className="form-input"
                                value={formData.format}
                                onChange={handleChange}
                            >
                                <option value="TPP">TPP</option>
                                <option value="FPP">FPP</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Prize Pool (₹)</label>
                            <input
                                type="number"
                                name="prize_pool"
                                className="form-input"
                                placeholder="0"
                                min="0"
                                value={formData.prize_pool}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Per Kill Price (₹)</label>
                        <input
                            type="number"
                            name="per_kill"
                            className="form-input"
                            placeholder="0"
                            min="0"
                            value={formData.per_kill}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Start Date & Time *</label>
                        <input
                            type="datetime-local"
                            name="start_ts"
                            className="form-input"
                            value={formData.start_ts}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            className="btn btn-outline"
                            onClick={onClose}
                            style={{ flex: 1 }}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-success"
                            style={{ flex: 1 }}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditMatchModal;
